import { forwardRef, ReactNode, Ref } from 'react';
import styled from 'styled-components';
import { useText } from '../../utils/hooks/use_text';
import type { IconDictionary } from '../icon';
import { Icon } from '../icon';
import { IconButton } from '../icon_button';
import { theme } from './notification_alert.theme';

const elements = {
	notificationAlert: styled.div`
		--borderColor: transparent;
		--background: transparent;
		--textColor: #000;
		--iconColor: #000;

		display: flex;
		width: auto;
		word-wrap: break-word;
		word-break: break-word;
		padding: ${theme.getValue('basePadding')};
		border-radius: ${theme.getValue('baseBorderRadius')};
		font: ${theme.getValue('baseText')};
		border: 1px solid var(--borderColor);
		background: var(--background);
		color: var(--textColor);

		& > .cellula-notification-alert__icon {
			color: var(--iconColor);
			flex-shrink: 0;
			align-self: center;
		}

		// Variants
		&[data-variant='info'] {
			--borderColor: ${theme.getValue('infoBorderColor')};
			--background: ${theme.getValue('infoBackground')};
			--textColor: ${theme.getValue('infoTextColor')};
			--iconColor: ${theme.getValue('infoIconColor')};
		}
		&[data-variant='success'] {
			--borderColor: ${theme.getValue('successBorderColor')};
			--background: ${theme.getValue('successBackground')};
			--textColor: ${theme.getValue('successTextColor')};
			--iconColor: ${theme.getValue('successIconColor')};
		}
		&[data-variant='warning'] {
			--borderColor: ${theme.getValue('warningBorderColor')};
			--background: ${theme.getValue('warningBackground')};
			--textColor: ${theme.getValue('warningTextColor')};
			--iconColor: ${theme.getValue('warningIconColor')};
		}
		&[data-variant='danger'] {
			--borderColor: ${theme.getValue('dangerBorderColor')};
			--background: ${theme.getValue('dangerBackground')};
			--textColor: ${theme.getValue('dangerTextColor')};
			--iconColor: ${theme.getValue('dangerIconColor')};
		}

		& > * {
			margin-right: ${theme.getValue('baseIconMargin')};
		}

		& > *:last-child {
			margin-right: 0;
		}
	`,
	body: styled.div`
		flex-grow: 1;
	`,
	closeButton: styled.div`
		flex-grow: 0;
	`,
};

type NotificationAlertVariant = 'info' | 'success' | 'warning' | 'danger';

interface WithoutClosable {
	closable?: never;
	onClose?: never;
}

interface Closable {
	/**
	 * This will render a close-button and you have to provide a callback for the click event. The alert will not be removed when clicking the close-button. You have to handle that your self with the `onClose` callback.
	 */
	closable?: boolean;

	/**
	 * Callback function to be trigger when clicking close-button. Only available when `closable` is set to `true`.
	 */
	onClose: () => void;
}

type NotificationAlertProps = {
	/**
	 * The visual representation of the alert.
	 */
	variant: NotificationAlertVariant;

	/**
	 * NOTE: Using this override is generally discouraged to maintain design consistency,
	 * only use this if you have a specific use-case that requires it.
	 *
	 * Override the default icon by providing an icon type from the IconDictionary.
	 * The icon color will still follow the variant color.
	 * @defaultValue undefined
	 */
	icon?: keyof IconDictionary;

	/**
	 * The content in the alert.
	 */
	children: ReactNode;
} & (Closable | WithoutClosable);

function CellulaNotificationAlert(
	{ variant, icon, closable = false, onClose, children, ...rest }: NotificationAlertProps,
	ref: Ref<HTMLDivElement>,
): JSX.Element | null {
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const staticTexts = useText();

	function handleClose(): void {
		if (onClose) {
			onClose();
		}
	}

	let iconType: keyof IconDictionary;

	if (icon) {
		iconType = icon;
	} else {
		switch (variant) {
			case 'success':
				iconType = 'check';
				break;
			case 'warning':
				iconType = 'warningCircle';
				break;
			case 'danger':
				iconType = 'warning';
				break;
			default:
				iconType = 'infoCircle';
		}
	}

	return (
		<elements.notificationAlert data-variant={variant || undefined} {...rest} ref={ref}>
			<div className='cellula-notification-alert__icon'>
				<Icon type={iconType} size='small' />
			</div>

			<elements.body>{children}</elements.body>
			{closable ? (
				<elements.closeButton>
					<IconButton variant={variant} size='small' icon='cross' text={staticTexts('Common.Close')} onClick={handleClose} />
				</elements.closeButton>
			) : null}
		</elements.notificationAlert>
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component to display alert messages to the user.
 */
const NotificationAlert = forwardRef(CellulaNotificationAlert);

export type { NotificationAlertProps };
export { NotificationAlert };
